import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { norm } from "@/lib/format";
import { Input } from "@/components/ui/input";

/** Selector de tutor con búsqueda por nombre, cédula/RNC, correo o nombre de los hijos. */
export const BuscadorTutor = ({ tutores, valor, onChange, testId = "buscador-tutor" }) => {
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState("");
  const caja = useRef(null);
  const elegido = tutores.find((t) => t.id === valor) || null;

  useEffect(() => {
    const fuera = (ev) => { if (caja.current && !caja.current.contains(ev.target)) setAbierto(false); };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, []);

  const resultados = useMemo(() => {
    const q = norm(texto.trim());
    if (!q) return tutores.slice(0, 40);
    return tutores.filter((t) => [t.nombre, t.cedula_rnc, t.email,
      ...(t.estudiantes || []).map((e) => `${e.nombre} ${e.matricula}`)]
      .filter(Boolean).some((v) => norm(v).includes(q))).slice(0, 40);
  }, [texto, tutores]);

  return (
    <div className="relative" ref={caja} data-testid={testId}>
      <button type="button" onClick={() => { setAbierto(!abierto); setTexto(""); }}
              data-testid={`${testId}-abrir`}
              className="flex w-full items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2 text-left text-sm hover:border-primary/50">
        <span className={elegido ? "min-w-0" : "text-muted-foreground"}>
          {elegido ? (
            <>
              <span className="font-medium">{elegido.nombre}</span>
              <span className="ml-2 font-mono text-xs text-muted-foreground">
                {elegido.cedula_rnc || "sin cédula/RNC"}
              </span>
              <span className="ml-2 text-xs text-muted-foreground">
                {(elegido.estudiantes || []).length} hijo(s)
              </span>
            </>
          ) : "Busque la familia por tutor, cédula/RNC o nombre del hijo…"}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>

      {abierto && (
        <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-md border border-border bg-card shadow-lg">
          <div className="relative border-b border-border/60 p-2">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input autoFocus value={texto} onChange={(e) => setTexto(e.target.value)}
                   data-testid={`${testId}-input`} className="pl-9"
                   placeholder="Tutor, cédula/RNC, correo o hijo…" />
          </div>
          <ul className="max-h-64 overflow-y-auto" data-testid={`${testId}-lista`}>
            {resultados.map((t) => (
              <li key={t.id}>
                <button type="button" data-testid={`opcion-tutor-${t.id}`}
                        onClick={() => { onChange(t.id); setAbierto(false); }}
                        className="flex w-full items-start justify-between gap-3 px-4 py-2.5 text-left text-sm hover:bg-muted">
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{t.nombre}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {t.cedula_rnc ? `Cédula/RNC ${t.cedula_rnc}` : "Sin cédula/RNC"}
                      {" · "}{(t.estudiantes || []).map((e) => e.nombre).join(", ") || "sin hijos"}
                    </span>
                  </span>
                  {t.id === valor && <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />}
                </button>
              </li>
            ))}
            {resultados.length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-muted-foreground"
                  data-testid={`${testId}-vacio`}>
                Ninguna familia coincide con la búsqueda.
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
